// User types
export type UserRole = 'parent' | 'caretaker' | 'family' | 'professional' | 'admin';

export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

// Available colors and emojis for caretakers
export const CARETAKER_COLORS = [
  '#EF4444', // red
  '#F97316', // orange
  '#3B82F6', // blue
  '#8B5CF6', // purple
  '#EC4899', // pink
];

export const CARETAKER_EMOJIS = [
  '🦊', // fox
  '🐼', // panda
  '🦁', // lion
  '🦉', // owl
  '🦋', // butterfly
];

// Role-specific colors
export const FAMILY_ROLE_COLOR = '#F59E0B'; // amber
export const PROFESSIONAL_ROLE_COLOR = '#14B8A6'; // teal
export const FAMILY_ROLE_HOURLY_RATE = 20;

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string | null;
  role: UserRole;
  other_role_label: string | null;
  hourly_wage: number | null;
  max_hours_per_month: number | null;
  preferred_days_off: DayOfWeek[];
  unavailable_days: DayOfWeek[];
  profile_picture_url: string | null;
  color: string | null;
  emoji: string | null;
  created_at: string;
  updated_at: string;
}

// Availability types
export interface AvailabilityWindow {
  start_time: string; // HH:MM format
  end_time: string; // HH:MM format
}

export interface AvailabilityTemplate {
  id: string;
  user_id: string;
  day_of_week: DayOfWeek;
  windows: AvailabilityWindow[];
  created_at: string;
  updated_at: string;
}

export interface AvailabilityOverride {
  id: string;
  user_id: string;
  date: string; // YYYY-MM-DD format
  is_available: boolean;
  windows: AvailabilityWindow[] | null;
  reason: string | null;
  created_at: string;
  updated_at: string;
}

// Time slot types
export type TimeSlotStatus = 'available' | 'claimed' | 'confirmed' | 'completed' | 'cancelled';

export interface TimeSlot {
  id: string;
  date: string; // YYYY-MM-DD format
  start_time: string; // HH:MM format
  end_time: string; // HH:MM format
  status: TimeSlotStatus;
  caretaker_id: string | null;
  caretaker?: User;
  notes: string | null;
  hours_worked?: number;
  pay_amount?: number;
  is_paid?: number;
  paid_at?: string | null;
  clock_in_time?: string | null;
  clock_out_time?: string | null;
  actual_start_time?: string | null;
  actual_end_time?: string | null;
  actual_hours_worked?: number | null;
  actual_pay_amount?: number | null;
  no_show?: number;
  created_at: string;
  updated_at: string;
}

export interface TimeSlotStats {
  total_hours: number;
  claimed_hours: number;
  confirmed_hours: number;
  completed_hours: number;
  available_hours: number;
}

// Auth types
export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  first_name: string;
  last_name?: string;
  role: UserRole;
  other_role_label?: string;
  hourly_wage?: number;
  max_hours_per_month?: number;
  preferred_days_off?: DayOfWeek[];
  unavailable_days?: DayOfWeek[];
  color?: string;
  emoji?: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

// API types
export interface ApiError {
  message: string;
  code?: string;
  status?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

// Form types
export interface UpdateUserRequest {
  first_name?: string;
  last_name?: string;
  other_role_label?: string;
  hourly_wage?: number;
  max_hours_per_month?: number;
  preferred_days_off?: DayOfWeek[];
  unavailable_days?: DayOfWeek[];
}

export interface CreateTemplateRequest {
  day_of_week: DayOfWeek;
  windows: AvailabilityWindow[];
}

export interface UpdateTemplateRequest {
  windows: AvailabilityWindow[];
}

export interface CreateOverrideRequest {
  date: string;
  is_available: boolean;
  windows?: AvailabilityWindow[];
  reason?: string;
}

export interface ClaimSlotRequest {
  time_slot_id: string;
}

export interface UpdateSlotStatusRequest {
  status: TimeSlotStatus;
  notes?: string;
}

// Message types
export interface Message {
  id: string;
  user_id: string;
  user?: User;
  content: string;
  mentioned_user_ids?: string[];
  created_at: string;
}

export interface SendMessageRequest {
  content: string;
  mentioned_user_ids?: string[];
}

// Notification types
export type NotificationType = 'mention' | 'shift_assigned' | 'shift_unassigned' | 'shift_reminder';

export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  body: string;
  reference_id?: string;
  reference_type?: string;
  is_read: number;
  created_at: string;
}

// Care log types
export type CareLogType = 'urine' | 'bm' | 'microseizure' | 'exercise' | 'meal' | 'medication' | 'note' | 'other';

export interface CareLog {
  id: string;
  slot_id: string | null;
  caretaker_id: string;
  log_type: CareLogType;
  notes: string | null;
  severity: string | null;
  quantity: string | null;
  logged_at: string;
  created_at: string;
  caretaker?: {
    id: string;
    first_name: string;
    last_name: string | null;
    emoji: string | null;
    color: string | null;
    role: string;
  };
}

// Shared task types
export type SharedTaskStatus = 'todo' | 'in_progress' | 'done';
export type SharedTaskPriority = 'low' | 'normal' | 'high' | 'urgent';
export type SharedTaskCategory = 'household' | 'kai_care' | 'shopping' | 'medical' | 'general';

export interface SharedTask {
  id: string;
  title: string;
  status: SharedTaskStatus;
  priority: SharedTaskPriority;
  category: SharedTaskCategory;
  due_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  created_by: {
    id: string;
    first_name: string;
    last_name: string | null;
    emoji: string | null;
    color: string | null;
    role: string;
  };
  assigned_to: {
    id: string;
    first_name: string;
    last_name: string | null;
    emoji: string | null;
    color: string | null;
    role: string;
  } | null;
  completed_by: {
    id: string;
    first_name: string;
    last_name: string | null;
    emoji: string | null;
  } | null;
}

// Schedule types
export type ScheduleItemCategory = 'meal' | 'diaper' | 'medication' | 'exercise' | 'sleep' | 'general';

export interface Schedule {
  id: string;
  name: string;
  created_by: string;
  item_count: number;
  assignments: ScheduleAssignment[];
  created_at: string;
  updated_at: string;
}

export interface ScheduleItem {
  id: string;
  schedule_id: string;
  time: string; // HH:MM
  title: string;
  description: string | null;
  category: ScheduleItemCategory;
  sort_order: number;
  created_at: string;
}

export interface ScheduleAssignment {
  id: string;
  schedule_id: string;
  day_of_week: number | null;
  date: string | null;
  assigned_by: string;
  created_at: string;
}

// User stats for caretakers
export interface UserStats {
  user_id: string;
  hours_this_month: number;
  hours_all_time: number;
  earned_this_month: number;
  earned_all_time: number;
}

export type UserFilter = 'all' | 'parent' | 'caretaker' | 'family' | 'professional';

export function getUserInitials(firstName: string, lastName?: string | null): string {
  const first = firstName?.charAt(0) || '';
  const last = lastName?.charAt(0) || '';
  return `${first}${last}`.toUpperCase();
}

export function getFullName(firstName: string, lastName?: string | null): string {
  return `${firstName} ${lastName || ''}`.trim();
}
